import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { type CreditCardOrganization, CreditCardOrganizationIcons } from '@cart-ui/CreditCardOrganizationIcons/CreditCardOrganizationIcons'
import { sanitizeToLineBreaksOnly } from '@cart-ui/RadioPayment/RadioPayment'
import { CreditCardholderFields } from '@credit-card-payments/components/CreditCardholderFields'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import type * as TapPay from '@dni-types/payment/tappay'
import { useApiECommerceUserPaymentTokens } from '@hooks-api/eCommerceV2/use-api-e-commerce-user-payment-tokens'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type UserPaymentTokenInfo } from '@hooks-api/types/dataModel/eCommercePayments'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { type PaymentMethod, PaymentMethodMap, PaymentMethodType, PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { CARD_SOURCE, cardholderFields, CheckoutPayCreditCardModule, ECPayCreditCardFields, isCkoFlowModule, TappayCreditCardFields, TappayCreditCardModule } from '@payments'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Radio } from '@pets-ui/Radio/Radio'
import { Select } from '@pets-ui/Select/Select'
import { SelectOption, type SelectOptionValue } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { typography } from '@pets-ui/tokens/typography.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom-v5'
import { useContextPayment } from '../../../../fp-b2b-shared/components/PaymentProvider/PaymentProvider'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { useCreditCardPayment } from '../../../../fp-b2b-shared/hooks/useCreditCardPayment'
import { getCurrentSelectedMethod, getModule } from '../../../../fp-b2b-shared/utils/utility-process-credit-card-pay-module'
import { paymentComponentStyle } from './PaymentMethodComponent'

const styles = stylex.create({
  creditCardBox: {
    padding: 12,
    borderRadius: 4,
    backgroundColor: colors.grey100,
  },
  expirationDate: {
    width: 80,
  },
  descriptionHtml: {
    color: colors.grey500,
    fontSize: typography.sizeBody,
    fontWeight: typography.weightRegular,
    lineHeight: typography.lineHeightBody,
  },
})
export const PaymentCreditCard = () => {
  const {
    resetCreditCardModule,
    selectedPayment,
    setSelectedPayment,
    paymentMethods,
    setPaymentExecution,
    setConfiguredPaymentModule,
    setCardValidation,
    cardValidation,
    isSdkInitialized,
    userPaymentTokenInfo,
    setUserPaymentTokenInfo,
    cardSource,
    setCardSource,
    setIsLoadingPayment,
    isCurrentCreditCardRemembered,
    setIsCurrentCreditCardRemembered,
    isProcessingCheckout,
    isLoadingPayment,
    setCreditCardUpdateInfo,
    setCreditCardholderInfo,
  } = useContextPayment()
  const { env, campaignId, countryCode, campaignCode, platform } = useCheckout()
  const checked = selectedPayment?.type === PaymentMethodType.CREDIT_CARD_ONCE
  const creditCardMethods = useMemo(() => paymentMethods.filter(method => method.type === PaymentMethodType.CREDIT_CARD_ONCE) || [], [paymentMethods])
  const [selectedMethodId, setSelectedMethodId] = useState<number>(creditCardMethods[0]?.id ?? 0)
  const paymentModule = getModule(selectedMethodId, creditCardMethods)
  const correspondingMethod = paymentMethods.find(method => method.type === PaymentMethodType.CREDIT_CARD_ONCE && method.id === selectedMethodId)
  const [creditCardOrganization, setCreditCardOrganization] = useState<CreditCardOrganization | undefined>()
  const [userPaymentTokenInfos, setUserPaymentTokenInfos] = useState<UserPaymentTokenInfo[]>([])
  const supportedCardOrganizations = useMemo(() => {
    const isTappay = paymentModule instanceof TappayCreditCardModule
    const isCheckoutPay = paymentModule instanceof CheckoutPayCreditCardModule
    if (isTappay) return ['visa', 'master', 'jcb'] as CreditCardOrganization[]
    if (isCheckoutPay) return ['visa', 'master', 'american express'] as CreditCardOrganization[]
    return []
  }, [paymentModule])
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const isShopManager = !!userInfo?.roles.includes('shop_manager')

  const { transactionId: paymentTransactionIdFromUrl } = useParams<{ transactionId?: string }>()
  const { orderId, orderType } = useFpB2bPaymentService({ campaignId, paymentTransactionIdFromUrl, campaignCode, platform })

  const { formState, control } = useFormContext<CheckoutOrder>()
  const billingEmail = useWatch({ control, name: 'billing_address_email' })

  const { appliedPaymentInfo } = useCreditCardPayment({
    orderId,
    orderType,
    selectedPaymentMethod: correspondingMethod,
    rememberPaymentCardToken: isCurrentCreditCardRemembered,
    isProcessingCheckout,
    setIsPaymentProcessing: setIsLoadingPayment,
    creditCardMethods,
    selectedMethodId,
    cardSource,
    userPaymentTokenInfo,
    isChecked: checked,
    setConfiguredPaymentModule,
    paymentSuccessUrl: window.location.href,
    paymentFallbackUrl: window.location.href,
    billingEmail,
  })

  const translateText: Record<string, string> = {
    creditCardNumber: '信用卡號',
    creditCardExpiration: '月份/年份',
    creditCardCode: '安全碼',
    rememberCard: '記住卡片',
    selectCardNumber: '選擇卡號',
    addNewCard: '新增卡號',
    endingIn: '的結尾為',
    exp: '到期日為',
    pleaseSelect: '請選擇',
    disableChangeCardTooltipText: '下單後將會自動延續您的訂閱，以記憶的信用卡號進行扣款，直到您取消續訂為止。您可以隨時取消訂閱，避免再被扣款。',
    cardholderName: '持卡人姓名',
    cardholderEmail: '持卡人 Email',
    cardholderPhone: '持卡人手機號碼',
  }

  const handleTappayOnUpdate = useCallbackEvent((data: TapPay.CardUpdate) => {
    setCardValidation({ ...cardValidation, tappay: data })
    setCreditCardOrganization(data.cardType as CreditCardOrganization)
    setCreditCardUpdateInfo?.(data)
  })

  useEffect(() => {
    const isTappay = paymentModule instanceof TappayCreditCardModule

    if (!isSdkInitialized || !isTappay) return

    if (isTappay) {
      paymentModule.addEventListener('cardUpdate', handleTappayOnUpdate)
    }

    return () => {
      if (isTappay) {
        paymentModule.removeEventListener('cardUpdate', handleTappayOnUpdate)
      }
    }
  }, [handleTappayOnUpdate, isSdkInitialized, paymentModule])

  const handleRememberPaymentCardTokenChange = useCallbackEvent((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsCurrentCreditCardRemembered(e.target.checked)
  })

  const configurePayment = useCallbackEvent(async (selectedMethodId: number, creditCardMethods: PaymentMethod[]) => {
    try {
      const currentSelectedMethod = getCurrentSelectedMethod(selectedMethodId, creditCardMethods)
      if (!currentSelectedMethod) {
        throw new Error('Missing current selected method')
      }
      if (!appliedPaymentInfo) {
        return
      }
      const paymentModule = getModule(selectedMethodId, creditCardMethods)
      if (!paymentModule) {
        throw new Error('Wrong paymentModule')
      }

      if (isCkoFlowModule(paymentModule)) {
        return
      }

      const { payment_transaction_id, user_payment_token_infos, payment_service_provider_info } = appliedPaymentInfo
      const { app_id, app_key, ecpay_token } = payment_service_provider_info
      const isMissingEcpayToken = currentSelectedMethod.service_type === PaymentServiceType.ECPAY && !ecpay_token
      const isMissingAppCredentials = currentSelectedMethod.service_type !== PaymentServiceType.ECPAY && (!app_id || !app_key)
      if (isMissingEcpayToken || isMissingAppCredentials) {
        throw new Error('Missing app_id, app_key')
      }
      const appKey = (currentSelectedMethod.service_type === PaymentServiceType.ECPAY ? ecpay_token : app_key) || ''
      setUserPaymentTokenInfos(user_payment_token_infos)
      paymentModule.configurePayment({
        appId: app_id || '',
        appEnv: env,
        appKey,
      })
      setPaymentExecution({
        payment_transaction_id,
        service_pay_info: null,
        user_payment_token_info: undefined,
        payment_success_url: '',
        payment_fallback_url: '',
      })
      setConfiguredPaymentModule(paymentModule)
    }
    catch (error) {
      console.error(`[STM PaymentCreditCardOnce] ${error}`)
    }
    finally {
      setIsLoadingPayment(false)
    }
  })
  const handleSelectPayment = useCallbackEvent(async () => {
    try {
      if (!campaignCode) {
        throw new Error('Missing campaign code')
      }
      const currentSelectedMethod = getCurrentSelectedMethod(selectedMethodId, creditCardMethods)
      if (!currentSelectedMethod) {
        throw new Error('Missing payment method')
      }
      if (!selectedPayment) {
        throw new Error('Missing selected payment')
      }
      setSelectedPayment(currentSelectedMethod)
      configurePayment(selectedMethodId, creditCardMethods)
    }
    catch (error) {
      console.error(`[STM PaymentCreditCardOnce] ${error}`)
    }
  })

  const handleSelectServiceProvider = useCallbackEvent((event: React.MouseEvent<HTMLDivElement>, newValue: string) => {
    event.stopPropagation()
    const value = Number(newValue)
    setSelectedMethodId(value)
    try {
      const selected = creditCardMethods.find(method => method.id === Number(value))
      if (!selected) throw new Error('Missing selected method')
      setSelectedPayment(selected)
      configurePayment(value, creditCardMethods)
    }
    catch (error) {
      console.error(`[STM PaymentCreditCardOnce] ${error}`)
    }
  })
  const setDefaultSelectPayment = useCallbackEvent(async () => {
    try {
      if (!campaignCode) {
        throw new Error('Missing campaign code')
      }
      const currentSelectedMethod = getCurrentSelectedMethod(selectedMethodId, creditCardMethods)
      if (!currentSelectedMethod) {
        throw new Error('Missing payment method')
      }
      if (!selectedPayment) {
        throw new Error('Missing selected payment')
      }
      setSelectedPayment(currentSelectedMethod)
      configurePayment(selectedMethodId, creditCardMethods)
    }
    catch (error) {
      console.error(`[STM PaymentCreditCardOnce] ${error}`)
    }
  })
  const handleInitPayment = useCallback(async () => {
    const shouldInit = selectedPayment?.type === PaymentMethodType.CREDIT_CARD_ONCE && isSdkInitialized
    if (!shouldInit) return
    setDefaultSelectPayment()
  }, [isSdkInitialized, selectedPayment?.type, setDefaultSelectPayment])

  // NOTE: init for default payment
  useEffect(() => {
    handleInitPayment()
  }, [handleInitPayment])

  useEffectOnce(() => {
    return () => {
      resetCreditCardModule()
    }
  })
  const validUserPaymentTokenList = useMemo(() => {
    return userPaymentTokenInfos?.filter((info) => {
      if (!info.expire_year || !info.expire_month) return false

      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1
      const cardYear = Number(info.expire_year)
      const cardMonth = Number(info.expire_month)
      const isExpired = cardYear < currentYear || (cardYear === currentYear && cardMonth < currentMonth)
      return !isExpired
    })
  }, [userPaymentTokenInfos])

  /**
   * 選擇已記住的卡號
   */
  const handleSelectUserPaymentTokenInfo = useCallbackEvent((newValue: SelectOptionValue) => {
    const selectedInfo = validUserPaymentTokenList?.find(info => info.payment_token_id === newValue)
    if (selectedInfo) {
      setUserPaymentTokenInfo(selectedInfo)
    }
  })
  const handleChangeCardSource = useCallbackEvent((value: SelectOptionValue) => {
    setCardSource(value as string)
    if (value === CARD_SOURCE.ADD_CARD) {
      setUserPaymentTokenInfo(undefined)
    }
  })
  const tappayErrorMessage = useMemo(() => {
    return {
      creditCardNumber: formState.errors.card?.creditCardNumber?.message,
      cardholderName: formState.errors.card?.cardholderName?.message,
      cardholderEmail: formState.errors.card?.cardholderEmail?.message,
      cardholderPhoneNumber: formState.errors.card?.cardholderPhoneNumber?.message,
    }
  }, [formState.errors.card?.cardholderEmail?.message, formState.errors.card?.cardholderName?.message, formState.errors.card?.cardholderPhoneNumber?.message, formState.errors.card?.creditCardNumber?.message])

  useEffect(() => {
    const updatePaymentTokenInfos = () => {
      if (!appliedPaymentInfo || !appliedPaymentInfo.user_payment_token_infos) return
      setUserPaymentTokenInfos(appliedPaymentInfo.user_payment_token_infos)
    }
    updatePaymentTokenInfos()
  }, [appliedPaymentInfo, userPaymentTokenInfos, validUserPaymentTokenList])

  const creditCardholderErrorMessage = useMemo(() => {
    return {
      cardholderName: formState.errors.card?.cardholderInfoComplete?.cardholderName?.message,
      cardholderEmail: formState.errors.card?.cardholderInfoComplete?.cardholderEmail?.message,
      cardholderPhone: formState.errors.card?.cardholderInfoComplete?.cardholderPhone?.message,
    }
  }, [
    formState.errors.card?.cardholderInfoComplete?.cardholderName?.message,
    formState.errors.card?.cardholderInfoComplete?.cardholderEmail?.message,
    formState.errors.card?.cardholderInfoComplete?.cardholderPhone?.message,
  ])
  const { destructuredData: userPaymentTokenList } = useApiECommerceUserPaymentTokens({
    country_code: countryCode,
  })

  const isTappay = paymentModule instanceof TappayCreditCardModule
  const rememberedCreditCardholderInfo = useMemo(() => {
    if (!userPaymentTokenInfo || !userPaymentTokenList) return undefined
    const token = userPaymentTokenInfo.payment_token_id
    const found = userPaymentTokenList.find(savedToken => savedToken.payment_token_id === token)?.cardholder
    if (!found) return undefined
    return { ...found, token }
  }, [userPaymentTokenInfo, userPaymentTokenList])

  const isRememberedCardholderValid = useMemo(() => {
    /**
     * 只有Tappay才會在結帳頁畫出cardholder info input fields
     * 因此非Tappay的payment gateways，如果帶有無效的cardholder info，不畫出cardholder info ui
     */
    if (!isTappay) return true
    const { token, name_en, phone_number_country_code, phone_number, email } = rememberedCreditCardholderInfo || {}
    const isValidContact = (phone_number_country_code && phone_number) || email
    return name_en && token && isValidContact
  }, [isTappay, rememberedCreditCardholderInfo])

  useEffect(() => {
    const info = rememberedCreditCardholderInfo || null
    setCreditCardholderInfo(isTappay ? info : null)
  }, [isTappay, rememberedCreditCardholderInfo, setCreditCardholderInfo])

  const renderCardholderFields = useCallback(() => {
    if (!rememberedCreditCardholderInfo) return null
    return (
      <CreditCardholderFields
        cardholderNameTitle={translateText.cardholderName}
        cardholderEmailTitle={translateText.cardholderEmail}
        cardholderPhoneTitle={translateText.cardholderPhone}
        cardholderNamePlaceholder={cardholderFields.name_en.placeholder}
        cardholderEmailPlaceholder={cardholderFields.email.placeholder}
        cardholderPhonePlaceholder={cardholderFields.phone.number.placeholder}
        cardholderCountryCodePlaceholder={cardholderFields.phone.country_code.placeholder}
        errorMessage={creditCardholderErrorMessage}
        cardholderInfo={rememberedCreditCardholderInfo}
        setCardholderInfo={setCreditCardholderInfo}
        cardholderEmailOrPhoneRequired="* Email 或手機號碼須擇一填寫"
      />
    )
  }, [creditCardholderErrorMessage, rememberedCreditCardholderInfo, setCreditCardholderInfo, translateText.cardholderEmail, translateText.cardholderName, translateText.cardholderPhone])
  return (
    <Box component="label" rootStyle={[paymentComponentStyle.root, paymentComponentStyle.radioPaymentChecked]}>
      <VStack flexGrow={1} gap={2.5}>
        <HStack alignItems="center" gap={2}>
          <Radio
            name="paymentCreditCard"
            checked={checked}
            value="paymentCreditCard"
            onChange={handleSelectPayment}
            disabled={isLoadingPayment || isProcessingCheckout}
          />
          <Text fontWeight={600} lineHeight="100%">{PaymentMethodMap[PaymentMethodType.CREDIT_CARD_ONCE]}</Text>
          <Box flexGrow={1} />
          <CreditCardOrganizationIcons organization={creditCardOrganization} supportedOrganizations={supportedCardOrganizations} />
        </HStack>
        {
          <VStack gap={1}>
            <Select
              value={String(selectedMethodId)}
              /* The default selected value is locked because ecpay credit card is disabled */
              disabled
            >
              {creditCardMethods.map(method => <SelectOption key={method.id} value={String(method.id)} onClick={handleSelectServiceProvider}>{method.name}</SelectOption>)}
            </Select>
            {correspondingMethod?.description && (
              <div {...stylex.props(styles.descriptionHtml)} dangerouslySetInnerHTML={{ __html: sanitizeToLineBreaksOnly(correspondingMethod.description) }} />
            )}
          </VStack>
        }
      </VStack>
      <VStack gap={checked ? 2 : 0}>
        {/* 灰底框只用於 Tappay/ECPay 的 inline 欄位，CheckoutPay 沒有東西可顯示。 */}
        <Box rootStyle={checked && !isCkoFlowModule(paymentModule) ? styles.creditCardBox : undefined}>
          <TappayCreditCardFields
            isVisible={checked && selectedPayment.service_type === PaymentServiceType.TAPPAY}
            expirationDateStyle={styles.expirationDate}
            errorMessage={tappayErrorMessage}
            canRememberPaymentCardToken={!isShopManager}
            onRememberPaymentCardTokenChange={isShopManager ? undefined : handleRememberPaymentCardTokenChange}
            userPaymentTokenInfos={isShopManager ? [] : validUserPaymentTokenList}
            renderFieldsOfRememberedCard={isRememberedCardholderValid ? undefined : renderCardholderFields}
            onChangeCardSource={handleChangeCardSource}
            selectedUserPaymentTokenInfo={userPaymentTokenInfo?.payment_token_id}
            onChangeSelectUserPaymentTokenInfo={handleSelectUserPaymentTokenInfo}
            cardSource={cardSource}
            translateText={translateText}
          />
          <ECPayCreditCardFields
            isVisible={checked && selectedPayment.service_type === PaymentServiceType.ECPAY}
          />
        </Box>
      </VStack>
    </Box>
  )
}
